// Shared date formatters for the mail dashboard.
//
// Rules (see bot/docs/dashboard.md → "Date formatting conventions"):
//   • Every date is DD/MM/YYYY (UK order, four-digit year) — 2026-11-03.
//   • List: today / yesterday → HH:mm, older → DD/MM/YYYY  e.g. 09/04/2026
//   • Full (reader sender line) → "Today · HH:mm" for today,
//                                  "Weekday DD/MM/YYYY · HH:mm" otherwise.
//   • formatDate / formatDateTime for everything else.
//
// Every consumer should import from this file rather than inlining toLocaleDateString
// calls, so the format stays consistent as the UI grows.

function pad2(n) { return n < 10 ? '0' + n : String(n); }

// For the compact message list / search columns.
// Bucket headers above each group already supply the day context
// ("Today", "Yesterday", …), so for the past two days we surface the
// time (HH:MM) instead of repeating "Yest" in every row. Older rows
// get DD/MM/YYYY.
//
// Runs once per list row per render, so it avoids toDateString() /
// toLocaleTimeString() (an Intl formatter per call): plain local-date
// arithmetic gives the same answer. en-GB 2-digit hour:minute is HH:mm on a
// 24-hour clock, which is what the pad2 pair produces.
export function formatListDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const yest = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  const sameDate = (a, b) => a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
  if (sameDate(d, now) || sameDate(d, yest)) {
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  }
  // DD/MM/YYYY regardless of year — locale-independent and unambiguous.
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

// For the reader's sender one-line (and anywhere a fuller label is wanted).
export function formatFullDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return `Today · ${time}`;
  const weekday = d.toLocaleDateString('en-GB', { weekday: 'short' });
  return `${weekday} ${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} · ${time}`;
}

// DD/MM/YYYY for any Date or date string; '' when missing or invalid.
export function formatDate(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

// DD/MM/YYYY HH:mm (24-hour); '' when missing or invalid.
export function formatDateTime(value) {
  const date = formatDate(value);
  if (!date) return '';
  const d = value instanceof Date ? value : new Date(value);
  return `${date} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// DD/MM/YYYY · HH:MM — used in the Order Drawer where we want to match
// OnPoint's order-view rendering exactly (4-digit year, not 2). Accepts
// ISO or "YYYY-MM-DD HH:MM:SS" strings, returns '' for invalid / null.
export function formatOrderDate(dateStr, { withTime = true } = {}) {
  if (!dateStr) return '';
  // OnPoint is inconsistent: most date columns are "YYYY-MM-DD HH:MM:SS"
  // but `fap-date` (and others) come back as "DD-MM-YYYY HH:MM:SS",
  // which Date won't parse. Normalise both to ISO before parsing.
  let s = String(dateStr).replace(' ', 'T');
  const ddmmyyyy = /^(\d{2})-(\d{2})-(\d{4})(T.*)?$/.exec(s);
  if (ddmmyyyy) s = `${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}${ddmmyyyy[4] || ''}`;
  const d = new Date(s);
  if (isNaN(d.getTime())) return String(dateStr);
  const date = `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
  if (!withTime) return date;
  const t = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  // If the source had no time component, skip the time half.
  if (!/\d{1,2}:\d{2}/.test(String(dateStr))) return date;
  return `${date} · ${t}`;
}
